/**
 * The single owner of every trash mutation (DV-2524). The card affordance, the
 * bulk action-bar row and the trash view all call through here, so optimistic
 * behavior, rollback and messaging cannot drift between them.
 *
 * Optimism split: a trash removes from the grid immediately (the user's intent
 * is unambiguous and rollback is precise), while a recover waits for the
 * response because only the server knows the new sortOrder.
 */
import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import type { RootState } from '../../../store/store';
import type { Asset } from '../../../types/asset.types';
import type { ImageAssetType } from '@/components/ui/carousel';
import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';
import { toImageAsset } from '../model/toImageAsset';
import { toTrashedAsset } from './toTrashedAsset';
import {
  addRecovered,
  reinsertAsset,
  removeAssets
} from '../slice/ImageAssetSlice';
import {
  usePermanentlyDeleteAssetMutation,
  useRecoverAssetMutation,
  useTrashAssetMutation
} from '../services/assetApi';
import { TRASH_RETENTION_DAYS } from './trashRetention';

type UseAssetTrashArgs = {
  photosetId: string | null;
  onPhotosetUnsynced: (photosetId: string) => void;
};

// RTK's own narrowing: a rejected mutation is either a FetchBaseQueryError
// (carries `status`) or a SerializedError (does not), so the `in` check is what
// distinguishes them without a cast.
const isFetchError = (error: unknown): error is FetchBaseQueryError =>
  typeof error === 'object' && error !== null && 'status' in error;

// Recover 400s on two conditions — the asset is not trashed (i.e. it was already
// restored, the reachable case for a single user double-clicking through the
// re-enable window) or its origin photoset is gone — and the response body does
// not distinguish them, so the copy names both.
const isValidationError = (error: unknown): boolean =>
  isFetchError(error) && error.status === 400;

export function useAssetTrash({
  photosetId,
  onPhotosetUnsynced
}: UseAssetTrashArgs) {
  const dispatch = useDispatch();
  const imageAssets = useSelector(
    (state: RootState) => state.imageAsset.imageAssets
  );
  const [trashAsset] = useTrashAssetMutation();
  const [recoverAsset] = useRecoverAssetMutation();
  const [permanentlyDeleteAsset] = usePermanentlyDeleteAssetMutation();
  // Fallback per-row disable, kept as inert defence-in-depth rather than for
  // an active case. Since DV-2524 §13 the row leaves the cached listing
  // optimistically, so this rarely gets a chance to matter — and the one
  // case that sounds like it would need it (the patch no-ops because the
  // listing itself errored and has no cache entry to patch) never has a
  // rendered row to disable either: TrashGrid renders zero TrashCards
  // whenever the listing is loading, erroring, or empty — exactly the
  // no-cache-entry state.
  const [busyAssetUUID, setBusyAssetUUID] = useState<string | null>(null);

  // The photoset dropdown is reachable from BOTH the photos bar and the trash
  // bar, so the user can switch sets while a request is in flight. When they do,
  // usePropertyAssets replaces the working set with the new photoset's photos —
  // and a rollback or a recover-append that lands afterward would inject a photo
  // the visible photoset does not own. (The rollback would not even land in the
  // right place: none of the new set's photos appear in the captured
  // `orderBefore`, so reinsertAsset falls through to its append branch.) The
  // result is a phantom card until reload, and a later drag-reorder sends an
  // assetUUID the photoset doesn't own → 400 + order rollback.
  //
  // So every mutation captures the photoset it was issued against and skips its
  // slice dispatch when this ref has moved on by the time the response settles.
  // The alert still fires either way: alerts are global, and the user is owed
  // the outcome of the action they took.
  const livePhotosetIdRef = useRef(photosetId);
  livePhotosetIdRef.current = photosetId;

  const markUnsynced = useCallback(() => {
    if (photosetId) onPhotosetUnsynced(photosetId);
  }, [photosetId, onPhotosetUnsynced]);

  const trashPhotos = useCallback(
    async (photos: ImageAssetType[]) => {
      if (photos.length === 0) return;
      const issuedForPhotosetId = photosetId;
      // The trash listing is photoset-keyed, so a delete without one has no
      // cache to patch and no entity scope server-side. Call sites are gated on
      // photoset context (canDelete), so this is a guard, not a path.
      if (!issuedForPhotosetId) return;
      // Capture the ORDER before the optimistic removal, so a rollback can put
      // each photo back where the user last saw it. An index would not survive
      // the removal — see reinsertAsset's comment in ImageAssetSlice.
      const orderBefore = imageAssets.map((image) => image.photoUUID);
      dispatch(removeAssets(photos.map((photo) => photo.photoUUID)));

      const outcomes = await Promise.all(
        photos.map((image) =>
          trashAsset({
            photosetUUID: issuedForPhotosetId,
            asset: toTrashedAsset(image, issuedForPhotosetId)
          })
            .unwrap()
            .then(() => true)
            .catch(() => {
              if (livePhotosetIdRef.current === issuedForPhotosetId) {
                dispatch(reinsertAsset({ image, orderBefore }));
              }
              return false;
            })
        )
      );

      const deleted = outcomes.filter(Boolean).length;
      const failed = outcomes.length - deleted;
      if (deleted > 0) markUnsynced();
      // ONE alert, always: the alert slice holds a single configuration, so
      // dispatching a success and a failure for the same batch would show only
      // whichever landed last. A partial failure has to report both halves in one
      // message or the user never learns which photos actually reached the Trash.
      if (failed === 0) {
        dispatch(
          showAlert({
            type: Variants.SUCCESS,
            message: `${
              deleted === 1 ? 'Photo' : `${deleted} photos`
            } moved to Trash — recover within ${TRASH_RETENTION_DAYS} days.`
          })
        );
        return;
      }
      if (deleted > 0) {
        dispatch(
          showAlert({
            type: Variants.ERROR,
            message: `${deleted} of ${
              outcomes.length
            } photos moved to Trash — ${failed} could not be deleted and ${
              failed === 1 ? 'has' : 'have'
            } been restored.`
          })
        );
        return;
      }
      dispatch(
        showAlert({
          type: Variants.ERROR,
          message:
            failed === 1
              ? 'Could not delete this photo — it has been restored. Please try again.'
              : `${failed} photos could not be deleted and have been restored. Please try again.`
        })
      );
    },
    [imageAssets, dispatch, trashAsset, markUnsynced, photosetId]
  );

  const recoverPhoto = useCallback(
    async (asset: Asset) => {
      const issuedForPhotosetId = asset.trashedFromPhotosetUUID ?? photosetId;
      if (!issuedForPhotosetId) return;
      setBusyAssetUUID(asset.assetUUID);
      try {
        // The response carries the server-assigned sortOrder; map it through the
        // same helper the page load uses so the recovered card is identical to
        // a freshly-fetched one.
        const recovered = await recoverAsset({
          photosetUUID: issuedForPhotosetId,
          asset
        }).unwrap();
        // Same mid-flight guard as the rollback above: only append when the grid
        // still shows the photoset this recover was issued against.
        if (livePhotosetIdRef.current === issuedForPhotosetId) {
          dispatch(addRecovered(toImageAsset(recovered, issuedForPhotosetId)));
        }
        markUnsynced();
        dispatch(
          showAlert({
            type: Variants.SUCCESS,
            message: 'Photo restored to the end of the photo set.'
          })
        );
      } catch (error: unknown) {
        dispatch(
          showAlert({
            type: Variants.ERROR,
            message: isValidationError(error)
              ? 'Could not restore this photo — it may have already been restored, or its photo set no longer exists.'
              : 'Could not restore this photo. Please try again.'
          })
        );
      } finally {
        setBusyAssetUUID(null);
      }
    },
    [recoverAsset, dispatch, markUnsynced, photosetId]
  );

  const purgePhoto = useCallback(
    async (asset: Asset) => {
      const issuedForPhotosetId = asset.trashedFromPhotosetUUID ?? photosetId;
      if (!issuedForPhotosetId) return;
      setBusyAssetUUID(asset.assetUUID);
      try {
        await permanentlyDeleteAsset({
          photosetUUID: issuedForPhotosetId,
          asset
        }).unwrap();
        // No unsynced mirror: the photo left the photoset when it was trashed,
        // so a permanent delete owes nothing further to either channel. The
        // photoset guard above exists only because the mutation now carries
        // photosetUUID as its cache key — this path still touches no slice
        // state, only the AssetTrash tag its mutation invalidates.
        dispatch(
          showAlert({
            type: Variants.SUCCESS,
            message: 'Photo permanently deleted.'
          })
        );
      } catch {
        dispatch(
          showAlert({
            type: Variants.ERROR,
            message:
              'Could not permanently delete this photo. Please try again.'
          })
        );
      } finally {
        setBusyAssetUUID(null);
      }
    },
    [permanentlyDeleteAsset, dispatch, photosetId]
  );

  return { trashPhotos, recoverPhoto, purgePhoto, busyAssetUUID };
}
